// src/hooks/useBackCloses.js
//
// Owner 2026-10-12: "when users go back it goes all the way to the module's
// main page". A view that opens INSIDE a page (a modal, a drawer, an in-page
// editor) leaves nothing in the browser history, so the Back button (or the
// phone's back gesture) leaves the whole page and the list the person was
// working in is gone.
//
// This hook keeps ONE extra history entry while the view is open. Back then
// closes the view and the page underneath is exactly as it was left (filters,
// search, scroll). Closing the view from inside (its own Close button, the
// backdrop, Escape) drops that entry again, so the stack never grows.
//
//   useBackCloses(isOpen, close);
//
// Works with react-router's BrowserRouter: the pushed entry carries the
// router's own state (usr / key / idx), so the router sees the same location
// before and after and nothing else re-renders or reloads.
import { useEffect, useRef } from "react";

const MARK = "__sswBackCloses";

export default function useBackCloses(isOpen, onClose) {
    const pushedRef = useRef(false);     // our entry is on the stack
    const skipPopRef = useRef(0);        // popstate events we caused ourselves
    const pendingOpenRef = useRef(false);
    const isOpenRef = useRef(Boolean(isOpen));
    const onCloseRef = useRef(onClose);
    isOpenRef.current = Boolean(isOpen);
    onCloseRef.current = onClose;

    useEffect(() => {
        const push = () => {
            try {
                const state = { ...(window.history.state || {}), [MARK]: true };
                window.history.pushState(state, "", window.location.href);
                pushedRef.current = true;
            } catch {
                /* history unavailable: Back simply behaves as before */
            }
        };

        if (isOpen && !pushedRef.current) {
            // A close is still in flight (history.back() is asynchronous): push
            // once its popstate has arrived, or the new entry would be the one
            // popped.
            if (skipPopRef.current > 0) pendingOpenRef.current = true;
            else push();
        } else if (!isOpen && pushedRef.current) {
            pushedRef.current = false;
            pendingOpenRef.current = false;
            if (window.history.state && window.history.state[MARK]) {
                skipPopRef.current += 1;
                window.history.back();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        const onPop = () => {
            if (skipPopRef.current > 0) {
                skipPopRef.current -= 1;
                if (pendingOpenRef.current && isOpenRef.current && !pushedRef.current) {
                    pendingOpenRef.current = false;
                    try {
                        const state = { ...(window.history.state || {}), [MARK]: true };
                        window.history.pushState(state, "", window.location.href);
                        pushedRef.current = true;
                    } catch { /* see above */ }
                }
                return;
            }
            if (pushedRef.current) {
                // The person pressed Back while the view was open: close it and
                // stay on the page.
                pushedRef.current = false;
                const fn = onCloseRef.current;
                if (typeof fn === "function") fn();
            }
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);
    // Unmounting with the view still open (a sidebar link while a modal is up)
    // leaves the entry in place; the next Back shows the same page once more,
    // which is harmless.
}
